/**
 * Driver monthly attrition tick — pure helper extracted from
 * hrSlice.rollMonthlyAttrition (11/02/2026 — refactor hrSlice).
 *
 * Decides which drivers resign at the end of the month based on their
 * satisfaction + fatigue, mitigated by the optional `turnover_reduction`
 * support bonus.
 *
 * Rules :
 *   • Drivers currently `active`, `training`, `vacation` or `sick` are
 *     SAFE — no mid-mission departures.
 *   • Drivers assigned to an `active` vehicle are also safe (their relation
 *     to the mission counts as protection).
 *   • Base monthly resignation probability ladder :
 *       sat < 25 → 16 %
 *       sat < 40 →  8 %
 *       sat < 55 →  3 %
 *       sat < 70 →  1.2 %
 *       else      →  0.3 %
 *     Fatigue >80 → ×2 multiplier.
 *   • Final probability = base × (1 - min(turnoverReduction, 0.85)).
 *
 * Pure : no `set()`, no `get()`. Returns the next slice patch and a
 * summary the caller can use for notifications / KPI tracking.
 *
 * @param {Object} state - Slice state (drivers, vehicles, totalDays,
 *   attritionHistory, firedDriversArchive, notifications).
 * @param {Object} deps
 * @param {number} deps.turnoverReduction - 0..1 share to subtract from base proba.
 * @param {Function} deps.buildArchiveEntry - (driver, totalDays) → archive obj.
 * @param {Function} deps.generateId - () → string.
 * @returns {{
 *   count: number,
 *   patch?: Object,
 *   resigned?: Array<{id, name, satisfaction}>,
 * }}
 */
export function runMonthlyAttrition(state, deps) {
  const { turnoverReduction, buildArchiveEntry, generateId } = deps;
  const cappedReduction = Math.min(0.85, turnoverReduction || 0);

  const resigned = [];
  (state.drivers || []).forEach(d => {
    // 11/02/2026 (fix user prod « chauffeurs qui disparaissent après avoir
    // été recrutés parfois même en missions ») — Ajout des statuts
    // manquants dans la liste de protection : 'holiday' (nouveaux congés
    // annuels Nadia), 'resting' (repos post-mission), 'on_break' (suspendu),
    // 'striking' (grève). Sans ça, un chauffeur en congés qui touchait la
    // condition satisfaction < 55 (fréquent après un refus de raise)
    // pouvait démissionner discrètement au 1er du mois → l'utilisateur
    // voyait son chauffeur disparaître de la flotte sans notification claire.
    // 'returning' est également protégé (retour de mission en cours).
    if (d.status === 'active'
        || d.status === 'training'
        || d.status === 'vacation'
        || d.status === 'sick'
        || d.status === 'holiday'
        || d.status === 'resting'
        || d.status === 'on_break'
        || d.status === 'striking'
        || d.status === 'returning') return;
    // 24/07/2026 — un chauffeur déjà en préavis (renégociation refusée ou
    // pression congés) suit SA procédure de départ : pas de démission
    // « double voie » par attrition qui court-circuiterait le préavis
    // visible et l'indemnité éventuelle.
    if (d.pendingDeparture) return;
    if (d.assignedVehicleId) {
      const v = (state.vehicles || []).find(vv => vv.id === d.assignedVehicleId);
      // Vehicle protection : plus large que 'active' seulement, on protège
      // aussi delivery / returning / transfer (véhicules en mission).
      if (v && (v.status === 'active' || v.status === 'delivery'
                || v.status === 'returning' || v.status === 'transfer')) return;
    }
    // 11/02/2026 — Grace period : nouveau chauffeur recruté depuis < 60 j
    // ne peut pas démissionner (période d'essai + acclimatation).
    // EXCEPTION : si la satisfaction est en effondrement (< 15), le
    // chauffeur peut partir immédiatement (dysharmonie sévère, ex. mauvais
    // fit de personnalité détecté en 2 semaines).
    const daysSinceHire = (state.totalDays || 0) - (d.hiredAt || 0);
    const sat = d.satisfaction ?? 60;
    if (daysSinceHire < 60 && sat >= 15) return;
    let baseProb;
    if (sat < 25) baseProb = 0.16;
    else if (sat < 40) baseProb = 0.08;
    else if (sat < 55) baseProb = 0.03;
    else if (sat < 70) baseProb = 0.012;
    else baseProb = 0.003;
    if ((d.fatigue ?? 0) > 80) baseProb *= 2;
    const effectiveProb = baseProb * (1 - cappedReduction);
    if (Math.random() < effectiveProb) {
      resigned.push(d);
    }
  });

  if (resigned.length === 0) return { count: 0 };

  const resignedIds = new Set(resigned.map(d => d.id));
  const remainingDrivers = (state.drivers || []).filter(d => !resignedIds.has(d.id));
  const updatedVehicles = (state.vehicles || []).map(v =>
    resignedIds.has(v.assignedDriverId)
      ? { ...v, assignedDriverId: null }
      : v
  );

  const newArchive = { ...(state.firedDriversArchive || {}) };
  resigned.forEach(d => {
    newArchive[d.dataId] = buildArchiveEntry(d, state.totalDays);
  });

  const notif = resigned.map(d => ({
    id: generateId(),
    type: 'driver_resigned',
    // 07/02/2026 — P0 fix : nom du chauffeur dans le titre (avant
    // « Démission » générique ne renseignait pas le bandeau).
    title: `${d.name} démissionne`,
    message: `${d.name} a démissionné (satisfaction ${Math.round(d.satisfaction || 0)} %). Pensez à investir dans les RH pour retenir vos équipes.`,
    driverName: d.name,
    driverId: d.id,
    day: state.totalDays,
    read: false,
  }));

  // Track resignations for the 12-month churn KPI.
  const attritionHistory = [
    ...((state.attritionHistory || []).filter(h => (state.totalDays - h.day) <= 360)),
    ...resigned.map(d => ({
      id: `${d.id}-${state.totalDays}`,
      driverName: d.name,
      day: state.totalDays,
      satisfaction: Math.round(d.satisfaction || 0),
    })),
  ];

  return {
    count: resigned.length,
    patch: {
      drivers: remainingDrivers,
      vehicles: updatedVehicles,
      notifications: [...(state.notifications || []).slice(-30), ...notif],
      attritionHistory,
      firedDriversArchive: newArchive,
    },
    resigned: resigned.map(d => ({ id: d.id, name: d.name, satisfaction: d.satisfaction })),
  };
}
